<div class="grid min-h-screen lg:grid-cols-2">
    <!-- Brand Panel -->
    <div
        class="bg-primary relative hidden flex-col justify-between p-10 text-white lg:flex"
    >
        <div class="flex items-center gap-3">
            <span
                class="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15"
            >
                <ng-icon name="lucideCar" class="h-5 w-5" />
            </span>
            <div>
                <p class="text-base leading-tight font-bold">Benefit Portal</p>
                <p class="text-xs text-white/70">Admin Portal</p>
            </div>
        </div>

        <div>
            <h1 class="max-w-md text-3xl leading-snug font-bold">
                Manage your benefits, all in one place.
            </h1>
            <p class="mt-3 max-w-md text-sm text-white/70">
                Benefits, clients, claims and approvals — everything you need to
                manage benefits efficiently.
            </p>

            <ul class="mt-8 space-y-4">
                <li class="flex items-center gap-3 text-sm text-white/90">
                    <span
                        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10"
                    >
                        <ng-icon name="lucideCalendarDays" class="h-4 w-4" />
                    </span>
                    Track and manage benefit claims in real time
                </li>
                <li class="flex items-center gap-3 text-sm text-white/90">
                    <span
                        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10"
                    >
                        <ng-icon name="lucideUsers" class="h-4 w-4" />
                    </span>
                    Keep client and benefit records organized
                </li>
                <li class="flex items-center gap-3 text-sm text-white/90">
                    <span
                        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10"
                    >
                        <ng-icon name="lucideClipboardList" class="h-4 w-4" />
                    </span>
                    Full benefit history and reporting
                </li>
            </ul>
        </div>

        <p class="text-xs text-white/50">
            © 2026 Benefit Portal. All rights reserved.
        </p>
    </div>

    <!-- Form Panel -->
    <div class="flex items-center justify-center bg-gray-50 px-4 py-12">
        <div
            class="w-full max-w-sm rounded-lg bg-white ring-1 ring-gray-200/70"
        >
            <div class="px-8 pt-8 pb-6 text-center">
                <span
                    class="bg-primary/10 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl lg:hidden"
                >
                    <ng-icon name="lucideCar" class="text-primary h-6 w-6" />
                </span>
                <h1 class="text-xl font-bold text-gray-900">Welcome back</h1>
                <p class="mt-1 text-sm text-gray-400">
                    Sign in to your admin account
                </p>
            </div>

            <form
                [formGroup]="loginForm"
                (ngSubmit)="onSubmit()"
                class="px-8 pb-8"
            >
                @if (errorMessage) {
                    <div
                        class="mb-4 rounded-md bg-red-50 px-3 py-2.5 text-sm text-red-600 ring-1 ring-red-600/20 ring-inset"
                    >
                        {{ errorMessage }}
                    </div>
                }

                <div class="mb-4">
                    <label
                        for="email"
                        class="mb-1.5 block text-[11px] font-semibold tracking-wider text-gray-400 uppercase"
                    >
                        Email
                    </label>
                    <div class="relative">
                        <ng-icon
                            name="lucideMail"
                            class="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-300"
                        />
                        <input
                            id="email"
                            type="email"
                            formControlName="email"
                            autocomplete="email"
                            class="focus:border-primary focus:ring-primary h-10 w-full rounded-lg border border-gray-300 pr-3 pl-9 text-sm text-gray-800 transition-colors placeholder:text-gray-300 focus:ring-1 focus:outline-none"
                            placeholder="admin@example.com"
                        />
                    </div>
                    @if (
                        loginForm.get('email')?.touched &&
                        loginForm.get('email')?.errors
                    ) {
                        <p class="mt-1 text-xs text-red-500">
                            @if (loginForm.get('email')?.errors?.['required']) {
                                Email is required
                            } @else if (
                                loginForm.get('email')?.errors?.['email']
                            ) {
                                Please enter a valid email
                            }
                        </p>
                    }
                </div>

                <div class="mb-5">
                    <label
                        for="password"
                        class="mb-1.5 block text-[11px] font-semibold tracking-wider text-gray-400 uppercase"
                    >
                        Password
                    </label>
                    <div class="relative">
                        <ng-icon
                            name="lucideLock"
                            class="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-300"
                        />
                        <input
                            id="password"
                            [type]="showPassword() ? 'text' : 'password'"
                            formControlName="password"
                            autocomplete="current-password"
                            class="focus:border-primary focus:ring-primary h-10 w-full rounded-lg border border-gray-300 pr-10 pl-9 text-sm text-gray-800 transition-colors placeholder:text-gray-300 focus:ring-1 focus:outline-none"
                            placeholder="••••••••"
                        />
                        <button
                            type="button"
                            (click)="showPassword.set(!showPassword())"
                            aria-label="Toggle password visibility"
                            class="absolute top-1/2 right-2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-gray-300 hover:text-gray-500"
                        >
                            <ng-icon
                                [name]="
                                    showPassword()
                                        ? 'lucideEyeOff'
                                        : 'lucideEye'
                                "
                                class="h-4 w-4"
                            />
                        </button>
                    </div>
                    @if (
                        loginForm.get('password')?.touched &&
                        loginForm.get('password')?.errors
                    ) {
                        <p class="mt-1 text-xs text-red-500">
                            Password is required
                        </p>
                    }
                </div>

                <button
                    hlmBtn
                    type="submit"
                    [disabled]="isLoading"
                    class="w-full"
                >
                    @if (isLoading) {
                        Signing in…
                    } @else {
                        Sign In
                    }
                </button>
            </form>
        </div>
    </div>
</div>
